import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bMerchantService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-merchant-service'
import { useCouponManualInput } from '@cart-services/hooks/useCouponManualInput'
import { type GetCouponManualInputExceptionMessageReturn } from '@cart-services/utils/errorHandlers'
import { CouponManualInput } from '@cart-ui/CouponManualInput/CouponManualInput'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCalculateCampaignCoupons } from '@hooks-api/fortune/useApiCalculateCampaignCoupons'
import { useApiCampaignGetCouponIdByCode } from '@hooks-api/fortune/useApiCampaignGetCouponIdByCode'
import { UsageMethod } from '@hooks-api/types/dataModel/coupon'
import { type InputProps } from '@pets-ui/Input/Input'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { useCallback, useMemo } from 'react'
import { useStmCart } from '../../CartProvider'

export const CouponManualInputCard = () => {
  const { campaignCode, campaignId, projectCode, countryCode, platform } = useStmCart()
  const { merchant } = useFpB2bMerchantService(campaignId, platform)
  const { calculateValue } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const { addManualInputCouponIds, removeManualInputCouponIds } = useFpB2bCouponService(
    campaignId,
    projectCode,
    countryCode,
    campaignCode,
    platform,
  )
  const { trigger: getCouponIdByCodeTrigger, isMutating: isGetCouponIdByCodeMutating } = useApiCampaignGetCouponIdByCode()
  const { trigger: calculateCouponsTrigger, isMutating: isCalculateCampaignCouponsMutating } = useApiCalculateCampaignCoupons({
    campaign_code: campaignCode,
  })
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { availableMergedCoupons } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)

  const getCouponIdByCode = useCallback((couponCode: string) => getCouponIdByCodeTrigger({
    project_code: projectCode,
    campaign_code: campaignCode,
    coupon_code: couponCode,
    merchant_id: merchant.index,
  }), [campaignCode, getCouponIdByCodeTrigger, merchant.index, projectCode])

  const calculateCoupons = useCallback((manualInputCouponIds: number[]) => calculateCouponsTrigger({
    ...calculateValue,
    manual_input_coupon_ids: manualInputCouponIds,
  }), [calculateValue, calculateCouponsTrigger])

  const onApplyCoupon = useCallback((manualInputCouponIds: number[]) => {
    addManualInputCouponIds(manualInputCouponIds)
  }, [addManualInputCouponIds])

  const onApplyCouponSuccess = useCallback((message: string) => {
    openSnackbar({ message, severity: 'success' })
  }, [openSnackbar])

  const onApplyCouponSuccessNotQualified = useCallback((message: string) => {
    openSnackbar({ message, severity: 'warning' })
  }, [openSnackbar])

  const onApplyCouponError = useCallback((message: string, manualInputCouponIds: number[]) => {
    removeManualInputCouponIds(manualInputCouponIds)
    openSnackbar({ message, severity: 'error' })
  }, [openSnackbar, removeManualInputCouponIds])

  const onGetCouponError = useCallback((exceptionMessage: GetCouponManualInputExceptionMessageReturn) => {
    openSnackbar({
      message: exceptionMessage.message,
      severity: 'error',
    })
  }, [openSnackbar])

  const currentManualInputCouponIds = useMemo(() =>
    availableMergedCoupons?.filter(coupon => coupon.usage_method === UsageMethod.MANUAL).map(coupon => coupon.coupon_id),
  [availableMergedCoupons])

  const {
    couponCode,
    setCouponCode,
    handleSubmit,
  } = useCouponManualInput({
    getCouponIdByCode,
    calculateCoupons,
    onApplyCoupon,
    onApplyCouponSuccess,
    onApplyCouponSuccessNotQualified,
    onApplyCouponError,
    onGetCouponError,
    currentManualInputCouponIds,
  })

  const handleChange: InputProps['onChange'] = useCallbackEvent((event) => {
    setCouponCode(event.target.value)
  })

  return (
    <CouponManualInput
      onSubmit={handleSubmit}
      value={couponCode}
      onChange={handleChange}
      disabled={isGetCouponIdByCodeMutating || isCalculateCampaignCouponsMutating}
      placeholder="請輸入折扣碼"
      submitText="使用"
    />
  )
}
